// Import the existing types and functions
import { ProductCategory } from '@/helper/enums';
import type { PageContractConclusionStoryblok } from '@/types/component-types-sb';

export type OrderSalutation = 'Herr' | 'Frau' | 'Divers' | 'keine' | '';

export interface PersonalData {
  salutation: OrderSalutation;
  firstname: string;
  lastname: string;
  birthday: string;
  title?: string;
  email?: string;
  contactPhone?: string;
  company?: string;
  supplement?: string;
  addContractPartner?: boolean;
  commercialRegisterNumber?: string;
  industry?: string;
}

export enum PageContractConclusionOrderType {
  ProviderChange = 'versorgerwechsel',
  NewTenant = 'neueinzug',
  Retention = 'vertragsabschluss',
  ProductChange = 'vertragswechsel',
}

// Enums
export enum CustomerType {
  Individual = 1,
  Organization = 2,
  Group = 3,
}

// Utility Functions

/**
 * Generic function to parse enum values from a string.
 */
export function parseEnumValue<T>(en: T, argument: string): T[keyof T] {
  const enumValues = en as unknown as { [s: string]: T[keyof T] };
  for (const entry of Object.entries(enumValues)) {
    const [key, value] = entry;
    if (argument === value) {
      return enumValues[key] as T[keyof T];
    }
  }
  throw new Error(`Unable to parse enum value from string: ${argument}`);
}

export function parseProductCategory(argument: string): ProductCategory {
  return parseEnumValue(ProductCategory, argument);
}

export type SELF_SERVICE_REG_TYPE = 'no_display' | 'display_agb' | 'display_checkbox';

export interface CustomerRecommendationViaCode {
  kwkCode?: string;
}

// Full OrderData type
export interface OrderData {
  orderType: PageContractConclusionOrderType;
  customerType: CustomerType;
  personalData: PersonalData;
  contractPartnerData: PersonalData;
  invoiceAddress: Address;
  paymentData: PaymentData;
  customerRecommendationViaCodeData: CustomerRecommendationViaCode;
  incentiveData: {
    code: string;
    value: string;
    descriptionText: string;
  };
  selfServiceRegistration: SELF_SERVICE_REG_TYPE;
  optInVisible: boolean;
  regulatedProducts: string;
  contractData: ContractData[];
}

/**
 * Interface for contract data
 */
export interface ContractData {
  shippingAddress: Address;
  deliveryDesiredBeginning: string;
  counterReading: string;
  counterNumber: string;
  utilitiesChangeAccountProvider: string;
  utilitiesChangeCancellationType: string;
  utilitiesChangeCancellationDoneWhen: string;
  tariffProductId: string;
  tariffProductName: string;
  tariffProductCategory: string;
  tariffProductBasePrice: string; // € per year
  tariffProductWorkingPrice: string; // Cent/kWh
  tariffProductMonthlyPrice: string; // € per month
  tariffProductYearlyPrice: string; // € per year
  tariffConsumption: string; // kWh per year
  tariffProductTargetGroup: string;
  tariffPricesDate: string;
  tariffProductBonus: string;
  tariffProductBonusDirect: string;
  textMsb: string;
  textProductNotice: string;
  textPriceGuarantee: string;
  terminationNotice: string;
  textAgb: string;
  textMinDuration: string;
  showOptional: boolean;
}

/**
 * Initial personal data
 */
export const initialPersonalData: PersonalData = {
  salutation: '',
  title: '',
  firstname: '',
  lastname: '',
  birthday: '',
  email: '',
  contactPhone: '',
};

export const initialContractPartnerPersonalData: PersonalData = {
  title: '',
  salutation: '',
  firstname: '',
  lastname: '',
  birthday: '',
};

/**
 * General interface for address data
 */
export interface Address {
  zip: string;
  city: string;
  additional: string;
  apartment?: string;
  street: string;
  hnr: string;
  differentInvoiceAddress?: boolean;
  country?: string;
  foreign?: string;
  isPostBox?: boolean;
  postBox?: string;
  commercialRegisterNumber?: string;
  industry?: string;
}

export const initialInvoiceAddress: Address = {
  country: 'Deutschland',
  zip: '',
  city: '',
  additional: '',
  street: '',
  hnr: '',
  isPostBox: false,
  postBox: '',
};

export interface PaymentData {
  iban: string;
  bic: string;
  bankName: string;
  sepaCheck: boolean;
  additionalComments: string;
}

export const initialPaymentData: PaymentData = {
  iban: '',
  bic: '',
  bankName: '',
  sepaCheck: false,
  additionalComments: '',
};

export const initialCustomerRecommendationViaCode: CustomerRecommendationViaCode = {
  kwkCode: '',
  solicitorGpNr: '',
  solicitorEmail: '',
  rewardType: '',
  solicitorSplittingFactor: 0,
  solicitorContractNr: '',
};

/**
 * Interface for user data
 */
export interface User {
  customerType: CustomerType;
  fullName: string;
  address: string;
  country: string;
  iban: string;
}

export enum SapOrderErrorCode {
  AlreadyConfirmed = 102,
  Expired = 103,
  Invalid = 104,
  GeneralError = 105,
}

/**
 * Interface for page props
 */
export interface PageContractConclusionProps {
  blok: PageContractConclusionStoryblok;
  urlParams: {
    orderGuid: any;
    processId: number;
    tokenGuid: string;
  };
  orderData?: OrderData;
  isMockMode?: boolean;
  mockKey?: string;
  mockFixture?: string;
}

export class SapOrderError extends Error {
  constructor(
    message: string,
    public code: number
  ) {
    super(message);
    this.name = 'SapOrderError';
  }
}

export interface OptIns {
  optInGeneralTerms: boolean;
  optInRevocationTerms: boolean;
  optInAds: boolean;
}

export interface CustomerRecommendationViaCode {
  kwkCode?: string;
  solicitorGpNr: string;
  solicitorEmail: string;
  rewardType: string;
  solicitorSplittingFactor: number;
  solicitorContractNr: string;
}

export interface OrderWarning {
  key: string;
  message: string;
}

export interface CustomerRecommendation {
  name: string;
  contractAccount: string;
}
